import Cookies from "js-cookie";

/**
 * The tracking-consent cookie is shared with salesably-home: a visitor who
 * accepts (or declines) on either salesably.ai or app.salesably.ai must have
 * that choice honoured on both. That only works if both apps write byte-for-byte
 * the same cookie, so this module is the single place either choice is written.
 *
 * The contract, matched by the salesably-home banner:
 *   name     tracking-consent
 *   value    "granted" | "not-granted"
 *   expires  365 days
 *   path     /
 *   domain   .salesably.ai — only when we are actually on a salesably.ai host
 *   sameSite lax
 *   secure   only over https
 *
 * One global code path: no geo or region branching. Global Privacy Control is
 * honoured everywhere, for everyone.
 *
 * app.salesably.ai is a paid product full of logged-in customers who have
 * already accepted. Every path here is written so that the worst case is a
 * visitor keeping the choice they already made — never being reset, and never
 * being asked again.
 */
export const CONSENT_COOKIE_NAME = "tracking-consent";
export const CONSENT_GRANTED = "granted";
export const CONSENT_NOT_GRANTED = "not-granted";

const CONSENT_COOKIE_EXPIRY_DAYS = 365;
const CONSENT_COOKIE_PATH = "/";
const SHARED_COOKIE_HOST = "salesably.ai";
const SHARED_COOKIE_DOMAIN = ".salesably.ai";

/**
 * Whether a stored value is a choice this contract recognises. Anything else —
 * a hand-edited cookie, a value from some older scheme — is not a decision the
 * visitor made here, and must not be treated as one.
 *
 * @param {string | undefined} value
 */
export function isRecordedConsent(value) {
  return value === CONSENT_GRANTED || value === CONSENT_NOT_GRANTED;
}

/**
 * The parent-domain cookie is only valid on hosts we actually serve. A browser
 * silently drops a cookie whose domain does not cover the current host, so on
 * localhost and on preview deploys (*.vercel.app) the domain must be omitted
 * entirely or consent breaks in dev.
 *
 * Matching is exact on the apex plus its subdomains, not a bare suffix test:
 * "notsalesably.ai" ends with the apex string but is not a host we own.
 *
 * @param {string | undefined | null} hostname
 * @returns {string | undefined} the domain attribute, or undefined to omit it
 */
export function getConsentCookieDomain(hostname) {
  if (!hostname) {
    return undefined;
  }

  const isSharedHost =
    hostname === SHARED_COOKIE_HOST ||
    hostname.endsWith(`.${SHARED_COOKIE_HOST}`);

  return isSharedHost ? SHARED_COOKIE_DOMAIN : undefined;
}

/**
 * @param {{ hostname?: string, protocol?: string }} location
 * @returns {object} js-cookie attributes for a consent write
 */
export function buildConsentCookieOptions({ hostname, protocol } = {}) {
  const options = {
    expires: CONSENT_COOKIE_EXPIRY_DAYS,
    path: CONSENT_COOKIE_PATH,
    sameSite: "lax",
  };

  const domain = getConsentCookieDomain(hostname);
  if (domain) {
    options.domain = domain;
  }

  if (protocol === "https:") {
    options.secure = true;
  }

  return options;
}

/**
 * Global Privacy Control, which CPRA requires us to honour. It is a per-request
 * signal, so it is re-read every visit and never frozen into a cookie.
 *
 * Undefined in most browsers, and some privacy tooling replaces `navigator`
 * wholesale, so the read is defensive.
 */
export function hasGlobalPrivacyControl() {
  try {
    return (
      typeof navigator !== "undefined" &&
      navigator?.globalPrivacyControl === true
    );
  } catch (error) {
    return false;
  }
}

function getBrowserLocation() {
  try {
    if (typeof window === "undefined" || !window.location) {
      return null;
    }

    return window.location;
  } catch (error) {
    return null;
  }
}

/**
 * Every value currently stored under the consent name, in the order the browser
 * lists them. More than one means a host-scoped cookie and a parent-domain
 * cookie are both present — the state this module exists to resolve. js-cookie
 * cannot report this: its `get` stops at the first match.
 *
 * @returns {string[]}
 */
function readConsentCookieEntries() {
  try {
    if (typeof document === "undefined" || typeof document.cookie !== "string") {
      return [];
    }

    const prefix = `${CONSENT_COOKIE_NAME}=`;

    return document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .filter((entry) => entry.startsWith(prefix))
      .map((entry) => entry.slice(prefix.length));
  } catch (error) {
    return [];
  }
}

/**
 * The value stored in the cookie, whatever its scope. Reads are domain-agnostic
 * — document.cookie hands back host-scoped and parent-domain cookies alike — so
 * this sees a choice made on either host.
 *
 * @returns {string | undefined}
 */
export function readTrackingConsentCookie() {
  try {
    if (typeof window === "undefined") {
      return undefined;
    }

    return Cookies.get(CONSENT_COOKIE_NAME);
  } catch (error) {
    return undefined;
  }
}

/**
 * The consent decision in force for this visit. GPC overrides anything stored,
 * so a visitor who signals it is treated as declined even if they accepted here
 * before.
 *
 * Most restrictive wins across every stored entry. While the cookie is migrating
 * to .salesably.ai a visitor can briefly hold both a host-scoped and a
 * parent-domain cookie, and those two can disagree; reading only the first would
 * let a stale "granted" enable tracking against a decline the visitor actually
 * recorded. Anything unrecognised counts as not granted too. Deciding what to
 * act on right now is a different question from deciding which record survives —
 * that second one is `normalizeTrackingConsentCookie`, which keeps the shared
 * cookie rather than the most restrictive one.
 *
 * @returns {string | undefined} undefined only when nothing at all is recorded
 */
export function resolveTrackingConsent() {
  if (hasGlobalPrivacyControl()) {
    return CONSENT_NOT_GRANTED;
  }

  const entries = readConsentCookieEntries();

  if (entries.length === 0) {
    return undefined;
  }

  return entries.every((value) => value === CONSENT_GRANTED)
    ? CONSENT_GRANTED
    : CONSENT_NOT_GRANTED;
}

/**
 * Deletes the pre-contract cookie. No domain option, matching how it was
 * originally written, so this only ever removes the host-scoped cookie and
 * leaves a parent-domain one alone.
 */
function removeLegacyHostScopedCookie() {
  Cookies.remove(CONSENT_COOKIE_NAME, { path: CONSENT_COOKIE_PATH });
}

/**
 * Records a choice on the shared cookie.
 *
 * Order matters and is the safety property of this whole change: the new cookie
 * is written FIRST, and the old host-scoped one is removed only once a second
 * cookie is actually visible, which is proof the new write landed. If the write
 * is dropped — a rejected domain on some host we did not anticipate — nothing is
 * removed and the visitor keeps the cookie, and the consent, they already had.
 *
 * @param {typeof CONSENT_GRANTED | typeof CONSENT_NOT_GRANTED} value
 */
export function writeTrackingConsent(value) {
  try {
    if (!isRecordedConsent(value)) {
      // This writes a cookie that both salesably.ai and app.salesably.ai read,
      // so a caller passing something off-contract would suppress the banner on
      // both hosts at once with a value neither side can act on. Refuse it.
      return;
    }

    if (hasGlobalPrivacyControl()) {
      // GPC is re-evaluated per visit rather than stored, so there is nothing
      // to write and nothing to clean up.
      return;
    }

    const location = getBrowserLocation();
    if (!location) {
      return;
    }

    const options = buildConsentCookieOptions({
      hostname: location.hostname,
      protocol: location.protocol,
    });

    Cookies.set(CONSENT_COOKIE_NAME, value, options);

    if (!options.domain) {
      // Host-scoped write: it replaced the cookie in place, so there is no
      // second copy to reconcile.
      return;
    }

    if (readConsentCookieEntries().length < 2) {
      // Either the parent-domain cookie was already the only one (nothing to
      // clean up) or the write did not land (removing anything now would strip
      // the visitor's only record). Both cases: leave it alone.
      return;
    }

    removeLegacyHostScopedCookie();

    if (readTrackingConsentCookie() === undefined) {
      // Belt and braces. If that removal somehow took the last copy with it,
      // put the choice back rather than leave the visitor with no record.
      Cookies.set(CONSENT_COOKIE_NAME, value, options);
    }
  } catch (error) {
    // Consent bookkeeping must never break rendering.
  }
}

/**
 * Brings an already-recorded choice onto the shared cookie without asking again,
 * and reports the decision in force. Safe to run on every page load.
 *
 * Existing customers accepted before this change and carry a host-scoped cookie.
 * Their choice still reads fine here, but salesably.ai cannot see it, so without
 * this they would be prompted again over there. The value is rewritten
 * unchanged: nobody is flipped, nobody is re-prompted, the cookie only widens
 * its scope.
 *
 * JavaScript cannot read a cookie's domain attribute, so there is no way to ask
 * whether a given cookie is already parent-scoped. The rewrite is therefore
 * unconditional and idempotent — on an already-migrated visitor it re-writes the
 * same value with the same attributes.
 *
 * @returns {string | undefined} the decision in force, if there is one
 */
export function normalizeTrackingConsentCookie() {
  try {
    if (hasGlobalPrivacyControl()) {
      // Treated as declined, and deliberately not written down.
      return CONSENT_NOT_GRANTED;
    }

    const entries = readConsentCookieEntries();

    if (entries.length > 1) {
      // A legacy host-scoped cookie is sitting alongside a parent-domain one —
      // which is what salesably-home writes, and its PR may land first. The
      // parent-domain cookie is the shared record, so drop only the legacy one
      // and never overwrite the shared cookie with a value read out of the
      // wrong scope.
      removeLegacyHostScopedCookie();

      const remaining = readTrackingConsentCookie();
      if (remaining !== undefined) {
        return remaining;
      }

      // Unreachable in a browser that scopes cookies the way we expect, but if
      // that removal cleared everything, restore the value rather than
      // re-prompt a customer who already answered.
      const recovered = entries.find(isRecordedConsent);
      if (recovered) {
        writeTrackingConsent(recovered);
      }

      return recovered;
    }

    const consent = readTrackingConsentCookie();

    if (isRecordedConsent(consent)) {
      writeTrackingConsent(consent);
    }

    // A value outside the contract is left exactly as it is rather than
    // propagated to the parent domain.
    return consent;
  } catch (error) {
    // Never break rendering; report whatever is still readable.
    return readTrackingConsentCookie();
  }
}
